<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      CSV Upload Batch Details <template v-if="batch">- {{ batch.transplant_center.code }} {{ batch.batch_date.display }}</template>
    </template>
    <template #body>
      <div v-if="batch" >
        <VeeForm @submit="handleFormSubmit">
          <div class="row mb-4">
            <div class="col-12 d-flex justify-content-between align-items-center">
              <span class='status-badge' :class="batch.batchStatusBadgeClass">{{ batch.batchStatusDisplayText }}</span>
            </div>
          </div>

          <div class="row">
            <div class="form-group-5-column-large">
              <label for="tc_code">Transplant Center</label>
              <input
                id="tc_code"
                type="text"
                class="form-control"
                :value="batch.transplant_center.code"
                disabled
              />
            </div>

            <div class="form-group-5-column-large">
              <label for="batch_date">Batch Date</label>
              <input
                id="batch_date"
                type="text"
                class="form-control"
                :value="batch.batch_date.display"
                disabled
              />
            </div>

            <div class="form-group-5-column-large">
              <label for="created_at">Created At</label>
              <input
                id="created_at"
                type="text"
                class="form-control"
                :value="batch.created_at.display"
                disabled
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <div  class="mb-2" v-if="batch.isPending || showReplaceForm">
                <label for="zip_file" class="form-label">Select ZIP File</label>
                <input
                  @change="handleBatchZipFileSelection"
                  type="file"
                  class="form-control"
                  id="zip_file"
                  accept=".zip"
                  :disabled="!(batch.isPending || showReplaceForm)"
                  required
                />
              </div>
              <div  class="mb-2" v-else>
                <label for="zip_file" class="form-label">Uploaded ZIP File</label>
                <input
                  type="text"
                  class="form-control"
                  id="zip_file"
                  readonly
                  :value="batch.uploaded_zip_details.stored_zip_file.stored_file.original_filename"
                />
                <div class="mt-2" v-if="batch.isZipFileReplaceable && !extractDispatched">
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="showReplaceForm = true">
                    Replace ZIP File
                  </button>
                </div>
              </div>
            </div>
          </div>
          <ActionToolbar :component-state="toolbarState"
            :success-message="successMessage"
            :secondary-enabled="true"
            :primary-button-text="nextStepText"
            :primary-enabled="(!batchError || showReplaceForm) && !batch.isCompleted"
            :secondary-button-text="secondaryButtonText"
            :error-message="formError || batchError"
            @secondary-click="handleSecondaryAction"
            @tertiary-click="!selectedAutoRuleOut.disabledAt ? onSubmitDisable() : onSubmitEnable()"
          />
        </VeeForm>
        <PortalTable
          table-id="extracted_files"
          :table-data="extractedFiles"
          :total-records="extractedFiles.length"
          :loading="isSavableStateProcessing"
          empty="No files extracted yet">
          <template #columns>
            <Column field="file_name" header="File Name" style="min-width: 200px;">
              <template #body="slotProps">
                {{ slotProps.data.file_name }}
              </template>
            </Column>
            <Column field="file_type" header="Type" style="min-width: 150px;">
              <template #body="slotProps">
                {{ slotProps.data.file_type }}
              </template>
            </Column>
            <Column field="status" header="Status" style="min-width: 120px;">
              <template #body="slotProps">
                <span :class="slotProps.data.batchStatusBadgeClass">
                  {{ slotProps.data.status }}
                </span>
              </template>
            </Column>
            <Column field="row_count" header="Row Count" style="min-width: 120px;">
              <template #body="slotProps">
                <div class="text-end">
                  {{ slotProps.data.row_count !== null ? slotProps.data.row_count.toLocaleString() : '-' }}
                </div>
              </template>
            </Column>
            <Column v-if="extractedFiles.some(file => file.file_error?.length)" field="file_error" header="Errors" style="min-width: 200px;">
              <template #body="slotProps">
                <span v-if="slotProps.data.file_error?.length" class="text-danger">
                  {{ slotProps.data.file_error.join('; ') }}
                </span>
              </template>
            </Column>
          </template>
        </PortalTable>
      </div>
    </template>
  </CardSection>
</template>

<script lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { EP, APIRoute } from '@/src/endpoints';
import { SaveStates } from '@/src/enums';
import beApiClient from '@/src/be-api-client';
import CardSection from "@/src/components/shared/CardSection.vue";
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import { formatDate, formatDateTimeTotoLocaleString } from '@/src/date-helpers';
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import { FinalizedOffersCSVBatch } from '@/src/models/unos/finalized-offers-csv-batch';
import { Form as VeeForm } from 'vee-validate';

export default {
  name: 'FinalizedOfferCSVBatchesEdit',
  mixins: [savableStateMixin],
  components: {
    CardSection,
    ActionToolbar,
    PortalTable,
    Column,
    VeeForm
  },
  methods: {
    setBatchFromAPI(responseData: any) {
      this.batch = FinalizedOffersCSVBatch.buildFromAPI(responseData);
    },
    async fetchBatch() {
      try {
        this.setSavableStateLoading();
        const url = APIRoute(EP.admin.finalized_offers_csv_batches.show, { id: this.$route.params.id })
        const response = await beApiClient.get(url)
        this.setBatchFromAPI(response.data);
        if (this.batch?.isFailed) {
          this.handleFormError(this.batchError);
        } else {
          this.setSavableStateIdle();
        }
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    handleBatchZipFileSelection(event: Event) {
      if(!event.target) return ;
      this.selectedFile = (event.target as HTMLInputElement).files?.[0] ?? null;
    },
    handleFormSubmit() {
      if(this.batch.isPending || this.showReplaceForm) this.handleZipUpload();
      else if(this.batch.isUploaded) this.triggerExtract();
      else this.triggerProcessing();
      //TODO: What else does this button do? Reprocess? Goes to another oage? Goes away?
    },
    handleSecondaryAction() {
      if (this.showReplaceForm) {
        this.showReplaceForm = false;
        this.selectedFile = null;
        this.setSavableStateIdle();
        return;
      }

      this.cancel();
    },
    async handleZipUpload() {
      const formData = new FormData();
      if (this.selectedFile) {
        formData.append('zip_file', this.selectedFile);
      }

      try {
        this.setSavableStateProcessing();
        const response = await beApiClient.post(APIRoute(EP.admin.finalized_offers_csv_batches.upload, { id: this.batch.id }), formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        this.setBatchFromAPI(response.data.data);
        this.showReplaceForm = false;
        this.successMessage = 'Successfully Uploaded zip file';
        this.setSavableStateSuccess();
      } catch (error: any) {
        this.handleFormError(error?.response?.data?.errors || error.message);
      }
    },

    async triggerExtract() {
      try {
        this.setSavableStateProcessing();
        const response = await beApiClient.post(APIRoute(EP.admin.finalized_offers_csv_batches.extract, { id: this.batch.id }));
        this.setBatchFromAPI(response.data.data);
        this.extractDispatched = true;
        this.setSavableStateIdle();
      } catch(error: any) {
        this.handleFormError(error?.response?.data?.errors || error.message);
      }
    },

    async triggerProcessing() {

      try {
        this.setSavableStateProcessing();
        const response = await beApiClient.post(APIRoute(EP.admin.finalized_offers_csv_batches.process_batch, { id: this.batch.id }));
        this.setBatchFromAPI(response.data.data);
        this.setSavableStateIdle();
      } catch(error) {
        this.handleFormError(error);
      }
    },

    cancel() {
      this.$router.push({ name: 'admin_unos_finalized_offers_csv_batch' });
    }
  },
  computed: {
    nextStepText(): string {
      if(this.batch.isPending || this.showReplaceForm) return 'Upload File';
      if(this.batch.isUploaded || this.batch.isExtracting) return 'Extract Files';
      if(this.batch.isStaged || this.batch.isProcessing) return 'Process Data';
      return 'Next';
    },
    toolbarState(): string {
      if(this.batch.isExtracting || this.batch.isProcessing) return SaveStates.Processing;
      return this.currentState;
    },
    secondaryButtonText(): string {
      return this.showReplaceForm ? 'Cancel' : 'Close';
    },
    extractedFiles(): Array<any> {
      // Alphabetical sort by file type for consistency
      return (this.batch.extracted_file_details?.extracted_files || []).slice().sort((a, b) => (a.file_type ?? '').localeCompare(b.file_type ?? ''))
    },
    batchError(): string | null {
      if (!this.batch?.isFailed) return null;
      const error = this.batch.uploaded_zip_details?.error;
      if (!error) return null;
      if (typeof error === 'object') return (error as any).error_message || 'An unknown error occurred';
      return String(error);
    }
  },
  mounted() {
    this.fetchBatch();
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const batch = ref<FinalizedOffersCSVBatch|null>(null);
    const loading = ref(true)
    const selectedFile = ref(null)
    const showReplaceForm = ref(false)
    const extractDispatched = ref(false)
    const isUploading = ref(false)
    const isExtracting = ref(false)
    const uploadError = ref('')

    return {
      batch,
      loading,
      formatDate,
      formatDateTime: formatDateTimeTotoLocaleString,
      selectedFile,
      showReplaceForm,
      extractDispatched,
      isUploading,
      isExtracting,
      uploadError,
      SaveStates
    }
  }
}
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  border-radius: 0.25rem;
  text-align: center;
  white-space: nowrap;
  text-transform: uppercase;
}
</style>

